import { Box, Typography, Autocomplete, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import ExcelUploadInput from '../../reuseable-components/ExcelInput/ExcelUploadInput';
import ExcelDownloadTemplateButton from '../../reuseable-components/ExcelInput/ExcelDownloadTemplateButton';
import SimpleAutocompleteProps from '../../reuseable-components/Autocomplete/SimpleAutocompleteProps';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';

ExcelPriceUpdate.propTypes = {
  open: PropTypes.bool,
  isCustomerUpload: PropTypes.bool,
  setOpen: PropTypes.func,
}

export default function ExcelPriceUpdate({ open, setOpen, isCustomerUpload }) {
  const gridRef = useRef(null);

  const [sheets, setSheets] = useState(null);
  const [headerRow, setHeaderRow] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [excelUploadData, setExcelUploadData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setExcelData([]);
    setHeaderRow([]);
    setExcelUploadData({});
    setSheets(null);
  }, [open]);

  useEffect(() => {
    if (headerRow?.length) setExcelUploadData(data => ({
      ...data,
      productIdCol: headerRow.find(cell => cell.name?.toLowerCase() === "product id")?.value ?? data.productIdCol,
      vendorCol: headerRow.find(cell => cell.name?.toLowerCase() === "vendor")?.value ?? data.vendorCol,
      vendorItemIdCol: headerRow.find(cell => cell.name?.toLowerCase() === "vendor item id")?.value ?? data.vendorItemIdCol,
      uomCol: headerRow.find(cell => cell.name?.toLowerCase() === "uom" || cell.name?.toLowerCase() === "uom type")?.value ?? data.uomCol,
      priceCol: headerRow.find(cell => cell.name?.toLowerCase() === "price")?.value ?? data.priceCol,
      customerCol: headerRow.find(cell => cell.name?.toLowerCase() === "customer" || cell.name?.toLowerCase() === "update customer prices (yes/no)")?.value ?? data.customerCol,
    }))
  }, [headerRow]);

  const submit = () => {
    setSubmitting(true);
    axiosInstance.post("/Product/ExcelBulkPriceUpdate", objectToFormData({
      sheet: excelUploadData.sheet,
      file: excelUploadData.file,
      isCustomerUpdate: isCustomerUpload,
      fields: [
        { field: "ProductId", col: excelUploadData.productIdCol },
        { field: "Vendor", col: excelUploadData.vendorCol },
        { field: "VendorItemId", col: excelUploadData.vendorItemIdCol },
        { field: "UOM", col: excelUploadData.uomCol },
        { field: "Price", col: excelUploadData.priceCol },
        { field: "Customer", col: excelUploadData.customerCol },
      ]
    }))
      .then(() => {
        enqueueSnackbar("Price update started. You will receive an email with the results once done.", { variant: "success" });
        setOpen(false);
      })
      .catch(() => enqueueSnackbar("Price update failed", { variant: "error" }))
      .finally(() => setSubmitting(false));
  }

  const columns = useMemo(() => headerRow.map(data => ({ field: data.value, headerName: data.label, flex: 1 })), [headerRow]);
  const rows = useMemo(() => excelData.length > 0 ? excelData.slice(1) : excelData, [excelData]);

  return <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
    <DialogTitle sx={{ textAlign: 'center' }}>
      {isCustomerUpload ? "Customer Price Update" : "Vendor Price Update"}
    </DialogTitle>
    <DialogContent>
      <Grid sx={{ mt: 0 }} container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center">
            <ExcelUploadInput
              setSheets={setSheets}
              setFile={(file) => setExcelUploadData(data => ({ ...(data ?? {}), file }))}
              setSelectedSheet={(sheet) => setExcelUploadData(data => ({ ...(data ?? {}), sheet }))}
              setHeaderRow={setHeaderRow}
              setExcelData={setExcelData}
              selectedSheet={excelUploadData?.sheet}
              ignoreEmptyRows
            />
            <ExcelDownloadTemplateButton fields={["Product Id", "Vendor Item Id", "UOM Type", "Vendor", "Price", isCustomerUpload ? "Customer*" : "Update Customer Prices (Yes/No)"]}
              fileName="Price Update Template.xlsx" worksheetName="Price Update" />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography>Sheet</Typography>
          <Autocomplete
            fullWidth
            value={excelUploadData?.sheet ? { value: excelUploadData.sheet, label: excelUploadData.sheet } : null}
            onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), sheet: newValue?.value }))}
            options={sheets?.map(sheet => ({ value: sheet, label: sheet })) ?? []}
            {...SimpleAutocompleteProps}
          />
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={4}>
            <Typography>Product Id</Typography>
            <Autocomplete
              value={excelUploadData?.productIdCol ? headerRow.find(cell => cell.value === excelUploadData.productIdCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), productIdCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Vendor</Typography>
            <Autocomplete
              value={excelUploadData?.vendorCol ? headerRow.find(cell => cell.value === excelUploadData.vendorCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), vendorCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Vendor Item Id</Typography>
            <Autocomplete
              value={excelUploadData?.vendorItemIdCol ? headerRow.find(cell => cell.value === excelUploadData.vendorItemIdCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), vendorItemIdCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>UOM Type</Typography>
            <Autocomplete
              value={excelUploadData?.uomCol ? headerRow.find(cell => cell.value === excelUploadData.uomCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), uomCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Price*</Typography>
            <Autocomplete
              value={excelUploadData?.priceCol ? headerRow.find(cell => cell.value === excelUploadData.priceCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), priceCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>         
          <Grid item xs={4}>
            <Typography>{isCustomerUpload ? "Customer*" : "Update Customer Prices"}</Typography>
            <Autocomplete
              value={excelUploadData?.customerCol ? headerRow.find(cell => cell.value === excelUploadData.customerCol) : null}
              onChange={(event, newValue) => setExcelUploadData(data => ({ ...(data ?? {}), customerCol: newValue?.value }))}
              options={headerRow}
              {...SimpleAutocompleteProps}
            />
          </Grid>   
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ height:300 }}>
            <AGGridQuickFilterWrapper gridRef={gridRef} />
             <Box sx={{ height: 270 }}>
              <AgGridReact ref={gridRef} {...AGGridDefaults} columnDefs={columns} rowData={rows} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={() => setOpen(false)}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!excelUploadData?.priceCol || !(excelUploadData.productIdCol ||
          excelUploadData.vendorCol && excelUploadData.vendorItemIdCol) || !(!isCustomerUpload || excelUploadData.customerCol)}
        sx={{ width: 150 }}
        onClick={submit}
        loading={submitting}
      >
        Save
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}
